import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  GQLArguments,
  GQLResponse,
  IGQLParams,
  IGQLResponse,
  IGQLSingleResponse,
} from '../../interfaces/graphql.interface';
import {
  getProviderBillingsQuery,
  getProviderQuery,
  getProvidersQuery,
} from '../../interfaces/provider/provider.graphql';
import {
  IBillingIds,
  IProvider,
  IQualifier,
  ProviderBillingsParams,
  ProviderFilters,
} from '../../interfaces/provider/provider.interfaces';
import { HelperGQLService } from '../helper.graphql.service';

@Injectable({
  providedIn: 'root',
})
export class ProviderGQLService {
  constructor(
    private apollo: Apollo,
    private gqlService: HelperGQLService,
  ) {}

  getProviders(
    providersParams?: IGQLParams<ProviderFilters>,
  ): Observable<GQLResponse<IProvider>> {
    const { orderby, direction, filter, after } = providersParams ?? {};
    const sortBy = orderby;
    const sortDirection = direction;

    const gqlArguments: GQLArguments = {
      first: 100,
    };

    if (filter) {
      const providerFilter: Record<string, unknown> = {
        providerGroupId: filter.groupId
          ? { eq: filter.groupId.toUpperCase() }
          : null,
        id: filter.id ? { eq: filter.id.toUpperCase() } : null,
        accountNumber: filter.accountNumber
          ? { eq: filter.accountNumber.toUpperCase() }
          : null,
        account: filter.accountName
          ? { name: { contains: filter.accountName } }
          : null,
        addressCity: filter.city
          ? { contains: filter.city.toUpperCase() }
          : null,
        feeSchedule: filter.feeSchedule ? { eq: filter.feeSchedule } : null,
        pms: filter.pMS ? { eq: filter.pMS } : null,
        contactName: filter.contactName
          ? { contains: filter.contactName.toUpperCase() }
          : null,
        contactPhone: filter.phone ? { eq: filter.phone.toUpperCase() } : null,
        contactEmail: filter.email
          ? { contains: filter.email.toUpperCase() }
          : null,
        addressZipcode: filter.zipcode
          ? { eq: filter.zipcode.toUpperCase() }
          : null,
        addressState: filter.state ? { eq: filter.state } : null,
      };
      if (filter.groupName) {
        providerFilter.providerGroup = { name: { contains: filter.groupName } };
      }
      gqlArguments.where = this.gqlService.cleanFilterObject(providerFilter);
    }

    if (sortBy && sortDirection) {
      gqlArguments.order = [{ [sortBy]: sortDirection }];
    }
    if (after) {
      gqlArguments.after = after;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IProvider>>({
        query: getProvidersQuery,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.providers;
        }),
      );
  }

  getProvider(id: string): Observable<IProvider> {
    return this.apollo
      .watchQuery<IGQLSingleResponse<IProvider>>({
        query: getProviderQuery,
        fetchPolicy: 'no-cache',
        variables: {
          id,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.provider;
        }),
      );
  }

  getQualifiers(): Observable<GQLResponse<IQualifier>> {
    return this.apollo
      .watchQuery<IGQLResponse<IQualifier>>({
        query: gql`
          {
            qualifiers(first: 500, order: { code: ASC }) {
              pageInfo {
                hasNextPage
                endCursor
              }
              nodes {
                code
                description
              }
            }
          }
        `,
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.qualifiers;
        }),
      );
  }

  getProviderBillings(
    providerBillingsParams: ProviderBillingsParams,
    status?: string,
  ): Observable<GQLResponse<IBillingIds>> {
    const gqlArguments: GQLArguments = {
      first: 500,
      order: { billingIdTypeCode: 'ASC' },
    };

    const providerFilter: Record<string, unknown> = {
      providerGroupId: { eq: providerBillingsParams.providerGroupId },
      providerId: { eq: providerBillingsParams.providerId },
      status: { eq: status },
    };

    gqlArguments.where = this.gqlService.cleanFilterObject(providerFilter);

    return this.apollo
      .watchQuery<IGQLResponse<IBillingIds>>({
        query: getProviderBillingsQuery,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.billingIds;
        }),
      );
  }
}
